import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import { userConstants } from './actions/userTypes';
import ProtectedRoutes from './utils/protectedRoutes';
import { authenticateUserWithToken } from './actions/auth';
import LoginForm from './components/login/loginForm';
import AdminDashboard from './components/admin/adminDashboard';
import MainWrapper from './components/wrappers/mainWrapper';
import Dashboard from './components/views/dashboard';
import ActiveClaims from './components/comparison/activeComparison';
import AllComparisons from './components/comparison/allComparisons';
import ArchivedClaims from './components/views/archivedClaims';
import InsuranceCompanies from './components/views/insuranceCompanies';
import Statistics from './components/views/statistics';
import UploadDocs from './components/comparison/estimateUpload';
import CreateNewComparison from './components/comparison/createNewComparison';
import ComparisonResults from './components/views/comparisonResults';
import LimitedResults from './components/views/limitedResults';
import RestructuredResults from './components/views/restructuredResults';
import RestructuredResultspdf from './components/views/restructuredResultspdf';
import RegisterForm from './components/registration/userRegister';
import UpdateForm from './components/views/userUpdate';
import Reports from './components/views/reports';
import ComparisonReports from './components/views/comparisonReports';
import Accounts from './components/views/accounts';
import AdminPanel from './components/admin/adminPanel';
import ForgotPassword from './components/login/forgotPass';
import ResetPass from './components/login/resetPass';
import PostRegistration from './components/registration/postRegistration';
import ContinueComparison from './components/views/continueComparison';
import EditAccount from './components/admin/editAccount';
import FilteredComparisons from './components/views/filteredComparisons';

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => !!state.token);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(authenticateUserWithToken(token));
    }
  }, [dispatch]);

  useEffect(() => {
    let timer;
    if (isAuthenticated) {
      timer = setTimeout(() => {
        dispatch({ type: userConstants.LOGOUT_USER });
      }, 7200000); // 2 hours
    }
    return () => clearTimeout(timer);
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const tooltip = document.createElement('div');
    Object.assign(tooltip.style, {
      position: 'fixed',
      left: '2px',
      bottom: '40%',
      backgroundColor: 'white',
      color: 'black',
      fontWeight: 'bold',
      fontSize: '24px',
      padding: '6px 16px',
      borderRadius: '0px',
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 150ms ease',
      width: '13%',
      zIndex: 9999,
      boxShadow: '2px 6px 12px rgba(0,0,0,0.9)',
    });
    document.body.appendChild(tooltip);
  
    let activeEl = null;
  
    const showTooltip = (el) => {
      if (!el) return;
      if (activeEl === el) return; // avoid unnecessary updates
  
      activeEl = el;
      let text = el.getAttribute('data-tooltip') || el.getAttribute('title');
      if (!text) return;
  
      // move original title to data-original-title
      if (el.getAttribute('title')) {
        el.setAttribute('data-original-title', text);
        el.removeAttribute('title');
      }
  
      tooltip.textContent = text;
      tooltip.style.opacity = '1';
    };
  
    const hideTooltip = () => {
      if (!activeEl) return;
      tooltip.style.opacity = '0';
      const original = activeEl.getAttribute('data-original-title');
      if (original) {
        activeEl.setAttribute('title', original);
        activeEl.removeAttribute('data-original-title');
      }
      activeEl = null;
    };
  
    const onMouseOver = (e) => {
      const el = e.target.closest('[data-tooltip], [title]');
      showTooltip(el);
    };
  
    const onMouseOut = (e) => {
      if (!activeEl) return;
      if (!e.relatedTarget || !activeEl.contains(e.relatedTarget)) {
        hideTooltip();
      }
    };
  
    document.addEventListener('mouseover', onMouseOver);
    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('blur', hideTooltip);
    window.addEventListener('mouseleave', hideTooltip);
  
    return () => {
      tooltip.remove();
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('blur', hideTooltip);
      window.removeEventListener('mouseleave', hideTooltip);
    };
  }, []);
  
  
  return (
    <Router>
      <Routes>
        <Route path="/" element={<ProtectedRoutes />}>
          <Route path="/" element={<Navigate replace to="/dashboard" />} />
          <Route path="/dashboard" element={<Navigate replace to="/createNewComparison" />} />
          <Route path="/clientDashboard" element={<MainWrapper />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/activeClaims" element={<ActiveClaims />} />
          <Route path="/allComparisons" element={<AllComparisons />} />
          <Route path="/createNewComparison" element={<CreateNewComparison />} />
          <Route path="/archivedClaims" element={<ArchivedClaims />} />
          <Route path="/insuranceCompanies" element={<InsuranceCompanies />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/adminDashboard" element={<AdminDashboard />} />
          <Route path="/comparisonResults" element={<ComparisonResults />} />
          <Route path="/restructuredResults" element={<RestructuredResults />} />
          <Route path="comparisonReports" element={<ComparisonReports />} />
          <Route path="/comparisonResults/:comparisonID" element={<ComparisonResults />} />
          <Route path="/limitedResults/:comparisonID" element={<LimitedResults/>}/>
          <Route path="/restructuredResults/:comparisonID" element={<RestructuredResults />} />
          <Route path="/adminPanel" element={<AdminPanel />} />
          <Route path="/editAccount/:id" element={<EditAccount />} />
          <Route path="/continueComparison/" element={<ContinueComparison />} />
          <Route path="/continueComparison/:continueCompID" element={<ContinueComparison />} />
          <Route path="/filteredComparisons/:comparisonID" element={<FilteredComparisons />} />
        </Route>

        <Route path="/login" element={<LoginForm />} />
        <Route path="/postRegistration" element={<PostRegistration/>} />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/update" element={<UpdateForm />} />
        <Route path="/estimates" element={<UploadDocs />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset/:token" element={<ResetPass />} />
        <Route path="/restructuredResultspdf/:comparisonID" element={<RestructuredResultspdf />} />
      </Routes>
    </Router>
  );
}

export default App;
